import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { CategoryChip } from "@/components/CommentCategories";
import { PdfReportButton } from "@/components/PdfReportButton";
import { ArrowLeft, MessageSquare, Filter } from "lucide-react";

const monthLabel = (ym) => {
    if (!ym) return "Të gjithë muajt";
    const [y, m] = ym.split("-");
    const months = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];
    return `${months[parseInt(m) - 1]} ${y}`;
};

export default function ParentComments() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Filters: default to current month, subject = "all"
    const currentYM = new Date().toISOString().slice(0, 7);
    const [monthFilter, setMonthFilter] = useState(currentYM);
    const [subjectFilter, setSubjectFilter] = useState("all");

    useEffect(() => {
        api.get(`/students/${id}/comments`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [id]);

    const { months, subjects, filtered } = useMemo(() => {
        if (!data) return { months: [], subjects: [], filtered: [] };
        const months = Array.from(new Set(data.comments.map((c) => c.created_at?.slice(0, 7)).filter(Boolean))).sort().reverse();
        const subjects = Array.from(new Set(data.comments.map((c) => c.subject_name).filter(Boolean))).sort();
        const filtered = data.comments.filter((c) => {
            if (monthFilter !== "all" && c.created_at?.slice(0, 7) !== monthFilter) return false;
            if (subjectFilter !== "all" && c.subject_name !== subjectFilter) return false;
            return true;
        });
        return { months, subjects, filtered };
    }, [data, monthFilter, subjectFilter]);

    return (
        <Layout>
            <Link to="/parent" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4" data-testid="back-to-overview">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>

            {loading ? <div className="text-slate-500">Duke ngarkuar…</div> : !data ? <div>Nuk u gjet.</div> : (
                <>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Komentet e mësuesve</div>
                            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">{data.student.name}</h1>
                        </div>
                        <PdfReportButton studentId={id} />
                    </div>

                    {/* Filters */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="comments-filters">
                        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 font-bold shrink-0">
                            <Filter className="w-4 h-4" /> Filtro
                        </div>
                        <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2 font-semibold flex-1 sm:flex-none" data-testid="comments-filter-month">
                            <option value="all">Të gjithë muajt</option>
                            {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                        </select>
                        <select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2 font-semibold flex-1 sm:flex-none" data-testid="comments-filter-subject">
                            <option value="all">Të gjitha lëndët</option>
                            {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <div className="ml-auto text-xs text-slate-500" data-testid="comments-result-count">
                            <span className="font-bold text-oxford">{filtered.length}</span> nga {data.total} komente
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="parent-no-comments">
                            <MessageSquare className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                            <div className="font-heading text-xl font-bold text-slate-800">Pa komente për këtë filtër</div>
                            <p className="text-sm text-slate-600 mt-1">Provoni të zgjidhni një muaj ose lëndë tjetër.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filtered.map((c) => (
                                <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 flex gap-3 sm:gap-4 items-start" data-testid={`comment-${c.id}`}>
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-oxford text-white flex items-center justify-center font-heading font-bold shrink-0 text-sm sm:text-base">
                                        {c.teacher_initials}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <span className="font-heading font-bold text-slate-900">{c.subject_name}</span>
                                            <span className="text-xs text-slate-500">• {c.teacher_name}</span>
                                            <span className="text-xs text-slate-400 font-mono">{c.created_at?.slice(0, 10)}</span>
                                        </div>
                                        <div className="mb-2"><CategoryChip category={c.category} testId={`comment-${c.id}-chip`} /></div>
                                        {c.text && <p className="text-slate-700 leading-relaxed text-sm sm:text-base">{c.text}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </Layout>
    );
}
